import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Autocomplete,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import AnalyticsAPI from '../../services/api/analytics-api';

const TimecardCorrectionModal = ({
  open,
  onClose,
  selectedCells,
  detailedRows,
  projectId,
  sourcePackageId,
  token,
  onSuccess,
}) => {
  // Step State
  const [moveType, setMoveType] = useState('full'); // 'full' or 'partial'
  const [partialHours, setPartialHours] = useState({}); // { tcId: { st, ot, dt } }

  // Targets dropdown state
  // Note: the target Activity is intentionally NOT selectable here — each moved
  // timecard row preserves its own original activity_id. Only the package (and
  // its phase/cost_code) changes during a correction.
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetPackages, setTargetPackages] = useState([]); // List of packages with areas
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState('');

  // Reason state
  const [reason, setReason] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Flatten the selected data into an array of timecard rows for display & processing
  const getSelectedRows = () => {
    const rows = [];
    if (!detailedRows || Object.keys(selectedCells).length === 0) return rows;

    for (const emp of detailedRows) {
      for (const day of emp.daily || []) {
        const key = `${emp.employee_id}|${day.date}`;
        const selectedIds = selectedCells[key];
        if (!selectedIds || selectedIds.length === 0) continue;

        for (const tc of day.timecard_rows || []) {
          if (selectedIds.includes(tc.id)) {
            rows.push({
              ...tc,
              employee_id: emp.employee_id,
              employee_name: `${emp.firstname} ${emp.lastname}`,
              date: day.date,
            });
          }
        }
      }
    }
    return rows;
  };

  const selectedData = getSelectedRows();

  // Initialize modal state on open
  useEffect(() => {
    if (open) {
      const initPartial = {};
      selectedData.forEach((r) => {
        initPartial[r.id] = {
          st: r.hours_st || 0,
          ot: r.hours_ot || 0,
          dt: r.hours_dt || 0,
        };
      });
      setPartialHours(initPartial);
      setMoveType('full');
      setSelectedAreaId('');
      setSelectedPackageId('');
      setReason('');
      setSubmitError('');
    }
  }, [open, selectedCells]); // eslint-disable-line

  // Fetch correction targets on open
  useEffect(() => {
    if (open && projectId) {
      const loadTargets = async () => {
        setTargetsLoading(true);
        try {
          const pkgs = await AnalyticsAPI.getCorrectionTargets(
            projectId,
            token
          );
          setTargetPackages(pkgs.packages || []);
        } catch (err) {
          console.error('Failed to load targets', err);
        } finally {
          setTargetsLoading(false);
        }
      };
      loadTargets();
    }
  }, [open, projectId, token]);

  // Derived options for dropdowns
  const uniqueAreas = Array.from(
    new Map(
      targetPackages
        .filter((p) => p.area_id)
        .map((p) => [p.area_id, { id: p.area_id, name: p.area_name }])
    ).values()
  );

  const availablePackages = targetPackages.filter(
    (p) => !selectedAreaId || p.area_id === selectedAreaId
  );

  // Look up the source package in the targets list so we can display its
  // name/area/description context to the user at the top of the modal.
  const sourcePackage = targetPackages.find(
    (p) => p.package_id === sourcePackageId
  );

  // Interaction handlers
  const handlePartialChange = (tcId, type, val) => {
    const numericVal = parseFloat(val) || 0;
    setPartialHours((prev) => ({
      ...prev,
      [tcId]: {
        ...prev[tcId],
        [type]: numericVal,
      },
    }));
  };

  const handleSubmit = async () => {
    setSubmitError('');

    if (!selectedTargetValid) {
      setSubmitError('Please select a valid target Package.');
      return;
    }

    if (reason.trim().length < 10) {
      setSubmitError('Please provide a reason of at least 10 characters.');
      return;
    }

    if (selectedPackageId === sourcePackageId) {
      setSubmitError(
        'Target package cannot be the same as the source package.'
      );
      return;
    }

    const corrections = selectedData.map((r) => {
      if (moveType === 'full') {
        return {
          timecardId: r.id,
          moveType: 'full',
        };
      } else {
        const ph = partialHours[r.id];
        return {
          timecardId: r.id,
          moveType: 'partial',
          hoursSt: ph.st,
          hoursOt: ph.ot,
          hoursDt: ph.dt,
        };
      }
    });

    const payload = {
      projectId,
      sourcePackageId,
      targetPackageId: selectedPackageId,
      correctionReason: reason,
      corrections,
    };

    setSubmitting(true);
    try {
      const res = await AnalyticsAPI.applyTimecardCorrections(payload, token);
      onSuccess(res);
      onClose();
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || 'Failed to submit correction.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Validations
  const selectedTargetValid = selectedPackageId !== '';
  let partialValid = true;
  if (moveType === 'partial') {
    selectedData.forEach((r) => {
      const ph = partialHours[r.id];
      if (!ph) {
        partialValid = false;
        return;
      }
      if (ph.st > r.hours_st || ph.st < 0) partialValid = false;
      if (ph.ot > r.hours_ot || ph.ot < 0) partialValid = false;
      if (ph.dt > r.hours_dt || ph.dt < 0) partialValid = false;
      if (ph.st === 0 && ph.ot === 0 && ph.dt === 0) partialValid = false;
    });
  }

  const isFormValid =
    selectedTargetValid &&
    reason.trim().length >= 10 &&
    partialValid &&
    selectedPackageId !== sourcePackageId;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxWidth: 680, width: 680 } }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#f1f5f9',
          borderBottom: '1px solid #e2e8f0',
          color: '#1e293b',
          fontWeight: 600,
        }}
      >
        Review & Move Timecards
      </DialogTitle>

      <DialogContent
        sx={{
          p: 4,
          pt: '32px !important',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {submitError && <Alert severity="error">{submitError}</Alert>}

        {/* SOURCE PACKAGE CONTEXT */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderLeft: '3px solid #f59e0b',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#92400e',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              display: 'block',
              mb: 0.5,
            }}
          >
            Moving From
          </Typography>
          {sourcePackage ? (
            <>
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}
              >
                {sourcePackage.autodesk_name}
              </Typography>
              {sourcePackage.description && (
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.25 }}>
                  {sourcePackage.description}
                </Typography>
              )}
              {sourcePackage.area_name && (
                <Typography
                  variant="caption"
                  sx={{ color: '#64748b', display: 'block', mt: 0.5 }}
                >
                  Area: <strong>{sourcePackage.area_name}</strong>
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              {targetsLoading
                ? 'Loading package details...'
                : 'Source package details unavailable.'}
            </Typography>
          )}
        </Box>

        {/* SECTION 1: Review */}
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}
          >
            1. Review Selected Items
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 200 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell align="right">ST</TableCell>
                  <TableCell align="right">OT</TableCell>
                  <TableCell align="right">DT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedData.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.employee_name}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{r.activity_name || '—'}</TableCell>
                    <TableCell align="right">
                      {Number(r.hours_st || 0).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      {Number(r.hours_ot || 0).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      {Number(r.hours_dt || 0).toFixed(1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* SECTION 2: Scope */}
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}
          >
            2. Scope of Move
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={moveType}
              onChange={(e) => setMoveType(e.target.value)}
            >
              <FormControlLabel
                value="full"
                control={<Radio color="warning" />}
                label="Move All Selected Hours"
              />
              <FormControlLabel
                value="partial"
                control={<Radio color="warning" />}
                label="Move Partial Hours"
              />
            </RadioGroup>
          </FormControl>

          {moveType === 'partial' && (
            <Box
              mt={2}
              p={2}
              bgcolor="#fffbeb"
              border="1px solid #fcd34d"
              borderRadius={1}
            >
              <Typography variant="body2" color="textSecondary" mb={2}>
                Edit the hours below to specify how much should be moved to the
                new package. The remaining hours will stay on the current
                package.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#92400e', fontWeight: 600 }}>
                      Employee / Date
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: '#92400e', fontWeight: 600 }}
                    >
                      Move ST
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: '#92400e', fontWeight: 600 }}
                    >
                      Move OT
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: '#92400e', fontWeight: 600 }}
                    >
                      Move DT
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedData.map((r) => {
                    const ph = partialHours[r.id] || { st: 0, ot: 0, dt: 0 };
                    const errSt = ph.st > (r.hours_st || 0) || ph.st < 0;
                    const errOt = ph.ot > (r.hours_ot || 0) || ph.ot < 0;
                    const errDt = ph.dt > (r.hours_dt || 0) || ph.dt < 0;

                    return (
                      <TableRow key={r.id}>
                        <TableCell sx={{ fontSize: 13 }}>
                          {r.employee_name} <br />
                          <span style={{ color: '#64748b' }}>{r.date}</span>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={ph.st}
                            onChange={(e) =>
                              handlePartialChange(r.id, 'st', e.target.value)
                            }
                            error={errSt}
                            helperText={errSt ? `Max ${r.hours_st}` : ''}
                            sx={{ width: 80 }}
                            disabled={(r.hours_st || 0) === 0}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={ph.ot}
                            onChange={(e) =>
                              handlePartialChange(r.id, 'ot', e.target.value)
                            }
                            error={errOt}
                            helperText={errOt ? `Max ${r.hours_ot}` : ''}
                            sx={{ width: 80 }}
                            disabled={(r.hours_ot || 0) === 0}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={ph.dt}
                            onChange={(e) =>
                              handlePartialChange(r.id, 'dt', e.target.value)
                            }
                            error={errDt}
                            helperText={errDt ? `Max ${r.hours_dt}` : ''}
                            sx={{ width: 80 }}
                            disabled={(r.hours_dt || 0) === 0}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {/* SECTION 3: Target */}
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}
          >
            3. Target Destination
          </Typography>

          {targetsLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography color="textSecondary">Loading packages...</Typography>
            </Box>
          ) : (
            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  size="small"
                  options={uniqueAreas}
                  getOptionLabel={(opt) => opt?.name || ''}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={
                    uniqueAreas.find((a) => a.id === selectedAreaId) || null
                  }
                  onChange={(_, newVal) => {
                    setSelectedAreaId(newVal ? newVal.id : '');
                    setSelectedPackageId('');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Area (Optional)"
                      placeholder="Search areas..."
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth size="small" required>
                <Autocomplete
                  size="small"
                  options={availablePackages}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.autodesk_name} (${opt.description})` : ''
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt.package_id === val.package_id
                  }
                  value={
                    availablePackages.find(
                      (p) => p.package_id === selectedPackageId
                    ) || null
                  }
                  onChange={(_, newVal) =>
                    setSelectedPackageId(newVal ? newVal.package_id : '')
                  }
                  getOptionDisabled={(opt) =>
                    opt.package_id === sourcePackageId
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Package *"
                      placeholder="Search packages..."
                    />
                  )}
                />
              </FormControl>
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, color: '#64748b' }}
          >
            Each moved timecard keeps its original activity — only the package
            (and its phase/cost code) changes.
          </Typography>
        </Box>

        {/* SECTION 4: Reason */}
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}
          >
            4. Reason for Correction
          </Typography>
          <TextField
            fullWidth
            required
            multiline
            rows={2}
            placeholder="Describe why these hours need to be moved (e.g., 'Foreman charged to wrong IWP initially')."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Required for audit trail. Minimum 10 characters."
            error={reason.trim().length > 0 && reason.trim().length < 10}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ color: '#64748b' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disableElevation
          onClick={handleSubmit}
          disabled={!isFormValid || submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? 'Submitting...' : 'Confirm Correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimecardCorrectionModal;
